import React, { useState } from "react";
import { ScrollText, Shuffle, ArrowUpRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

// Simplified 22 Major Arcana
const DECK = [
  { n: 0, name: "The Fool", meaning: "New beginnings, innocence, spontaneity. A leap into the unknown awaits.", key: "Fresh start" },
  { n: 1, name: "The Magician", meaning: "Manifestation, willpower, resourcefulness. You have all the tools you need.", key: "Manifest" },
  { n: 2, name: "The High Priestess", meaning: "Intuition, sacred knowledge, subconscious mind. Trust the inner voice.", key: "Listen within" },
  { n: 3, name: "The Empress", meaning: "Femininity, abundance, nurturing. Creation blooms around you.", key: "Fertility" },
  { n: 4, name: "The Emperor", meaning: "Authority, structure, control. Time to take charge with discipline.", key: "Leadership" },
  { n: 5, name: "The Hierophant", meaning: "Tradition, spiritual wisdom, mentor. Seek guidance from teachers.", key: "Guru" },
  { n: 6, name: "The Lovers", meaning: "Love, harmony, choices. A pivotal decision of the heart approaches.", key: "Union" },
  { n: 7, name: "The Chariot", meaning: "Determination, willpower, victory. Move forward with focused intent.", key: "Victory" },
  { n: 8, name: "Strength", meaning: "Courage, patience, inner strength. Gentle power overcomes brute force.", key: "Grit" },
  { n: 9, name: "The Hermit", meaning: "Introspection, solitude, guidance. Withdraw to find your truth.", key: "Silence" },
  { n: 10, name: "Wheel of Fortune", meaning: "Cycles, destiny, turning point. What goes down, must come up.", key: "Karma turns" },
  { n: 11, name: "Justice", meaning: "Truth, fairness, cause & effect. Balance is being restored.", key: "Truth" },
  { n: 12, name: "The Hanged Man", meaning: "Pause, surrender, new perspective. Let go to receive.", key: "Surrender" },
  { n: 13, name: "Death", meaning: "Endings, transformation, rebirth. Something must die for new life.", key: "Rebirth" },
  { n: 14, name: "Temperance", meaning: "Balance, moderation, patience. Blend opposites into harmony.", key: "Alchemy" },
  { n: 15, name: "The Devil", meaning: "Attachment, addiction, illusion. Chains are self-made — you can free yourself.", key: "Break free" },
  { n: 16, name: "The Tower", meaning: "Sudden change, revelation, upheaval. Old structures crumble to reveal truth.", key: "Awakening" },
  { n: 17, name: "The Star", meaning: "Hope, inspiration, healing. Faith restored, light returns.", key: "Hope" },
  { n: 18, name: "The Moon", meaning: "Illusion, dreams, subconscious. Not everything is as it seems.", key: "Intuition" },
  { n: 19, name: "The Sun", meaning: "Joy, success, vitality. Radiant success shines upon you.", key: "Bliss" },
  { n: 20, name: "Judgement", meaning: "Awakening, reflection, calling. A new phase of life beckons.", key: "Rebirth call" },
  { n: 21, name: "The World", meaning: "Completion, fulfillment, wholeness. A cycle beautifully closes.", key: "Wholeness" },
];

const SPREADS = [
  { key: "single", label: "Card of the Day", count: 1, positions: ["Today's Message"] },
  { key: "past-present-future", label: "Past · Present · Future", count: 3, positions: ["Past", "Present", "Future"] },
  { key: "situation-action-outcome", label: "Situation · Action · Outcome", count: 3, positions: ["Situation", "Action", "Outcome"] },
];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function TarotService() {
  const [spread, setSpread] = useState(SPREADS[0]);
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState({});

  const draw = () => {
    const picked = shuffle(DECK).slice(0, spread.count);
    setCards(picked);
    setFlipped({});
  };

  return (
    <div className="relative min-h-screen">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-b from-[#1a0f1f] via-[#12151C] to-[#050507] border-b border-purple-500/20">
        <div className="absolute inset-0 starfield opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20">
          <div className="text-xs uppercase tracking-[0.35em] text-[#D4AF37] mb-4">Card Wisdom</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tighter max-w-3xl">
            Ask the deck. <span className="italic gold-gradient">Let the cards speak.</span>
          </h1>
          <p className="mt-6 max-w-xl text-[#A0A4AB]">
            22 Major Arcana. Shuffle with intention, draw with an open heart.
          </p>
        </div>
      </div>

      {/* Spread selector */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Choose a spread</div>
        <div className="grid md:grid-cols-3 gap-3 mb-10">
          {SPREADS.map((s) => (
            <button
              key={s.key}
              data-testid={`tarot-spread-${s.key}`}
              onClick={() => { setSpread(s); setCards([]); }}
              className={`p-6 rounded-xl border text-left transition ${
                spread.key === s.key
                  ? "border-[#D4AF37] bg-[#D4AF37]/10"
                  : "border-white/10 bg-[#0A0C10] hover:border-white/25"
              }`}
            >
              <ScrollText className="text-[#D4AF37]" size={20}/>
              <div className="font-serif text-lg mt-3">{s.label}</div>
              <div className="text-xs text-[#A0A4AB] mt-1">{s.count} card{s.count > 1 ? "s" : ""}</div>
            </button>
          ))}
        </div>

        <div className="flex items-center justify-center mb-12">
          <button
            data-testid="tarot-draw-btn"
            onClick={draw}
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition"
          >
            <Shuffle size={16}/> Shuffle & Draw
          </button>
        </div>

        {/* Cards */}
        {cards.length > 0 && (
          <div data-testid="tarot-result" className={`grid gap-6 ${cards.length === 1 ? "md:grid-cols-1 max-w-md mx-auto" : "md:grid-cols-3"}`}>
            {cards.map((c, i) => {
              const isFlipped = flipped[i];
              return (
                <div key={i} className="text-center">
                  <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-3">{spread.positions[i]}</div>
                  <button
                    onClick={() => setFlipped({ ...flipped, [i]: true })}
                    className="w-full aspect-[2/3] rounded-2xl relative overflow-hidden transition-transform hover:-translate-y-1"
                    style={{ perspective: 1000 }}
                  >
                    {!isFlipped ? (
                      <div className="w-full h-full rounded-2xl bg-gradient-to-br from-[#1a0f1f] via-[#12151C] to-[#0A0C10] border border-[#D4AF37]/40 flex items-center justify-center relative overflow-hidden">
                        <div className="absolute inset-0 starfield opacity-30" />
                        <div className="relative">
                          <div className="w-24 h-24 rounded-full border-2 border-[#D4AF37]/40 flex items-center justify-center slow-spin">
                            <Sparkles className="text-[#D4AF37]" size={30} strokeWidth={1}/>
                          </div>
                          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mt-4">Tap to reveal</div>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-full rounded-2xl bg-gradient-to-b from-[#12151C] to-[#0A0C10] border-2 border-[#D4AF37] p-6 flex flex-col">
                        <div className="text-xs text-[#D4AF37] uppercase tracking-widest">#{c.n}</div>
                        <div className="flex-1 flex items-center justify-center py-4">
                          <div className="text-6xl">🔮</div>
                        </div>
                        <div className="font-serif text-2xl text-[#EAE6DF]">{c.name}</div>
                        <div className="text-xs text-[#D4AF37] mt-1">{c.key}</div>
                      </div>
                    )}
                  </button>
                  {isFlipped && (
                    <p className="text-sm text-[#A0A4AB] mt-4 leading-relaxed">{c.meaning}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-16 text-center">
          <Link to="/astrologers" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 hover:border-[#D4AF37] hover:text-[#D4AF37] transition">
            Get a Live Tarot Reading <ArrowUpRight size={14}/>
          </Link>
        </div>
      </div>
    </div>
  );
}
